import { Work } from '@/types/work'
import moment, { Moment } from 'moment-timezone'
import styles from './timeBarSlots.module.scss'
import { OccasionalSlot, RegularCancellationRequest, RegularSlot } from '@/types/Slots'
import { CalendarEventType } from '@/types/Calendar'
import {
    getPreparationTimeAfterRange,
    getPreparationTimeBeforeRange,
    TimeRange
} from '@/modules/helper/workPreparationTimeHelper'
import { defineWorkTypeStyle, defineWorkTypeToCalendarEventType } from '@/container/planning/utils/eventsWorks'

export type TimBarSlots = {
    id?: number
    start: string
    end: string
    backgroundStyle: string
    type?: CalendarEventType
    isPreparation?: boolean
}

export function getEffectiveDayBounds(
    works: Work[],
    opening: string,
    closing: string
): { opening: string; closing: string } {
    let effectiveOpening: Moment = moment(opening, 'HH:mm')
    let effectiveClosing: Moment = moment(closing, 'HH:mm')

    works.forEach((work: Work): void => {
        const start: Moment = work.preparationTimeBefore
            ? moment(work.start, 'HH:mm').subtract(work.preparationTimeBefore, 'minutes')
            : moment(work.start, 'HH:mm')
        const end: Moment = work.preparationTimeAfter
            ? moment(work.end, 'HH:mm').add(work.preparationTimeAfter, 'minutes')
            : moment(work.end, 'HH:mm')

        if (start.isBefore(effectiveOpening)) effectiveOpening = start
        if (end.isAfter(effectiveClosing)) effectiveClosing = end
    })

    return { opening: effectiveOpening.format('HH:mm'), closing: effectiveClosing.format('HH:mm') }
}

export function defineStaffSlots(staffSlots: Work[], opening: string, closing: string): TimBarSlots[] {
    const slots: TimBarSlots[] = []

    for (let i: number = 0; i < staffSlots.length; i++) {
        const work: Work = staffSlots[i]
        const eventType: CalendarEventType = defineWorkTypeToCalendarEventType(work.type)

        const preparationBeforeRange: TimeRange | null = work.preparationTimeBefore
            ? getPreparationTimeBeforeRange(moment(work.start, 'HH:mm').toDate(), work.preparationTimeBefore)
            : null
        const preparationAfterRange: TimeRange | null = work.preparationTimeAfter
            ? getPreparationTimeAfterRange(moment(work.end, 'HH:mm').toDate(), work.preparationTimeAfter)
            : null

        const slotStart: string = preparationBeforeRange
            ? preparationBeforeRange.start
            : moment(work.start, 'HH:mm').format('HH:mm')
        const slotEnd: string = preparationAfterRange
            ? preparationAfterRange.end
            : moment(work.end, 'HH:mm').format('HH:mm')

        if (i === 0 && moment(slotStart, 'HH:mm').isAfter(moment(opening, 'HH:mm'), 'minutes')) {
            slots.push({
                start: opening,
                end: slotStart,
                backgroundStyle: styles.Transparent
            })
        }

        if (
            slots.length > 0 &&
            !moment(slotStart, 'HH:mm').isSame(moment(slots[slots.length - 1].end, 'HH:mm'), 'minutes')
        ) {
            slots.push({
                start: moment(slots[slots.length - 1].end, 'HH:mm').format('HH:mm'),
                end: slotStart,
                backgroundStyle: styles.Transparent
            })
        }

        if (preparationBeforeRange) {
            slots.push({
                id: work.id,
                start: preparationBeforeRange.start,
                end: preparationBeforeRange.end,
                backgroundStyle: styles.PreparationTask,
                type: eventType,
                isPreparation: true
            })
        }

        slots.push({
            id: work.id,
            start: moment(work.start, 'HH:mm').format('HH:mm'),
            end: moment(work.end, 'HH:mm').format('HH:mm'),
            backgroundStyle: `${styles.StaffWork} ${defineWorkTypeStyle(eventType)}`.trim(),
            type: eventType
        })

        if (preparationAfterRange) {
            slots.push({
                id: work.id,
                start: preparationAfterRange.start,
                end: preparationAfterRange.end,
                backgroundStyle: styles.PreparationTask,
                type: eventType,
                isPreparation: true
            })
        }

        if (i === staffSlots.length - 1 && moment(slotEnd, 'HH:mm').isBefore(moment(closing, 'HH:mm'), 'minutes')) {
            slots.push({
                start: slotEnd,
                end: closing,
                backgroundStyle: styles.Transparent
            })
        }
    }

    return slots
}

export function defineChildSlots(
    occasionalSlots: OccasionalSlot[],
    regularSlots: RegularSlot[],
    opening: string,
    closing: string,
    date: string
): TimBarSlots[] {
    const mergedSlots: TimBarSlots[] = []

    const day: number = (moment(date, 'DD-MM-YYYY').day() + 6) % 7

    occasionalSlots.map((slot: OccasionalSlot) => {
        mergedSlots.push({
            id: slot.id,
            start: moment(slot.start, 'HH:mm').format('HH:mm'),
            end: moment(slot.end, 'HH:mm').format('HH:mm'),
            backgroundStyle: styles.ValidatedSlot,
            type: CalendarEventType.VALIDATED
        })
    })

    regularSlots
        .filter((slot: RegularSlot) => slot.day === day)
        .map((slot: RegularSlot) => {
            const request: RegularCancellationRequest[] = slot.cancellationRequests.filter(
                (cancellationRequest: RegularCancellationRequest) =>
                    moment(cancellationRequest.date, 'DD-MM-YYYY').format('DD-MM-YYYY') === date
            )

            if (request.length > 0) {
                request.map((cancellationRequest: RegularCancellationRequest) => {
                    mergedSlots.push({
                        id: slot.id,
                        start: moment(cancellationRequest.start, 'HH:mm:ss').format('HH:mm'),
                        end: moment(cancellationRequest.end, 'HH:mm:ss').format('HH:mm'),
                        backgroundStyle: styles.RegularRequest,
                        type: CalendarEventType.REGULAR_REQUEST
                    })
                })
            } else if (
                slot.absence &&
                !mergedSlots.find(
                    (timeBarSlot: TimBarSlots): boolean => timeBarSlot.type === CalendarEventType.VALIDATED
                )
            ) {
                mergedSlots.push({
                    id: slot.id,
                    start: moment(slot.start, 'HH:mm').format('HH:mm'),
                    end: moment(slot.end, 'HH:mm').format('HH:mm'),
                    backgroundStyle: styles.Absence,
                    type: CalendarEventType.ABSENCE
                })
            } else {
                mergedSlots.push({
                    id: slot.id,
                    start: moment(slot.start, 'HH:mm').format('HH:mm'),
                    end: moment(slot.end, 'HH:mm').format('HH:mm'),
                    backgroundStyle: styles.Regular,
                    type: CalendarEventType.REGULAR
                })
            }
        })

    const slots: TimBarSlots[] = []

    mergedSlots.sort((a, b) => moment(a.start, 'HH:mm').diff(moment(b.start, 'HH:mm')))

    for (let i: number = 0; i < mergedSlots.length; i++) {
        if (i === 0 && moment(mergedSlots[i].start, 'HH:mm').isAfter(moment(opening, 'HH:mm'), 'minutes')) {
            slots.push({
                start: opening,
                end: moment(mergedSlots[i].start, 'HH:mm').format('HH:mm'),
                backgroundStyle: styles.Transparent
            })
        }

        if (
            slots.length > 0 &&
            !moment(mergedSlots[i].start, 'HH:mm').isSame(moment(slots[slots.length - 1].end, 'HH:mm'), 'minutes')
        ) {
            slots.push({
                start: moment(slots[slots.length - 1].end, 'HH:mm').format('HH:mm'),
                end: moment(mergedSlots[i].start, 'HH:mm').format('HH:mm'),
                backgroundStyle: styles.Transparent
            })
        }

        slots.push(mergedSlots[i])

        if (
            i === mergedSlots.length - 1 &&
            moment(mergedSlots[i].end, 'HH:mm').isBefore(moment(closing, 'HH:mm'), 'minutes')
        ) {
            slots.push({
                start: moment(mergedSlots[i].end, 'HH:mm').format('HH:mm'),
                end: closing,
                backgroundStyle: styles.Transparent
            })
        }
    }

    return slots
}

export function defineAvailableSlots(availableSlot: OccasionalSlot, opening: string, closing: string): TimBarSlots[] {
    const slots: TimBarSlots[] = []

    if (moment(availableSlot.start, 'HH:mm').isAfter(moment(opening, 'HH:mm'), 'minutes')) {
        slots.push({
            start: opening,
            end: moment(availableSlot.start, 'HH:mm').format('HH:mm'),
            backgroundStyle: styles.Transparent
        })
    }

    slots.push({
        id: availableSlot.id,
        start: moment(availableSlot.start, 'HH:mm').format('HH:mm'),
        end: moment(availableSlot.end, 'HH:mm').format('HH:mm'),
        backgroundStyle: defineAvailableSlotsStyle(availableSlot.type),
        type: availableSlot.type
    })

    if (moment(availableSlot.end, 'HH:mm').isBefore(moment(closing, 'HH:mm'), 'minutes')) {
        slots.push({
            start: moment(availableSlot.end, 'HH:mm').format('HH:mm'),
            end: closing,
            backgroundStyle: styles.Transparent
        })
    }

    return slots
}

function defineAvailableSlotsStyle(type: CalendarEventType): string {
    switch (type) {
        case CalendarEventType.EXPIRED:
            return styles.ExpiredSlot
        case CalendarEventType.REMAINDER_AVAILABLE:
            return styles.RemainderAvailableSlot
        case CalendarEventType.SCHEDULED:
            return styles.ScheduledSlot
        default:
            return styles.AvailableSlot
    }
}
